import type * as React from 'react';
import { useEffect, useLayoutEffect, useMemo } from 'react';

import './semanticColors.css';
import type { CookbookContext } from '@creditornot/cb-context';
import { CookbookContextProvider } from '@creditornot/cb-context';
import {
  DATA_ATTR_HEADING_FONT,
  DATA_ATTR_THEME,
  resolveHeadingFontByLocale,
} from '@creditornot/cb-utils';

const useIsomorphicLayoutEffect = typeof window === 'object' ? useLayoutEffect : useEffect;

type CookbookProviderProps = {
  children?: React.ReactNode;
  /** Control text direction and icons that imply directionality */
  direction?: CookbookContext['direction'];
  /**
   * `locale` affects the chosen heading font
   *
   * @default 'en'
   **/
  locale?: string;
  themeMode: CookbookContext['themeMode'];
};

export const CookbookProvider = ({
  children,
  direction = 'ltr',
  locale,
  themeMode = 'light',
}: CookbookProviderProps) => {
  useIsomorphicLayoutEffect(() => {
    document.documentElement.setAttribute(DATA_ATTR_THEME, themeMode);
  }, [themeMode]);

  useIsomorphicLayoutEffect(() => {
    if (locale) {
      document.documentElement.setAttribute('lang', locale);
    }

    document.documentElement.setAttribute(
      DATA_ATTR_HEADING_FONT,
      resolveHeadingFontByLocale(locale ?? 'en'),
    );
  }, [locale]);

  useIsomorphicLayoutEffect(() => {
    document.documentElement.setAttribute('dir', direction);
  }, [direction]);

  const contextValue: CookbookContext = useMemo(
    () => ({ direction, themeMode }),
    [direction, themeMode],
  );

  return <CookbookContextProvider value={contextValue}>{children}</CookbookContextProvider>;
};
